import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FilterSet } from '#server/models';
import { FilterHeader, FilterSection, CheckboxFilter, SliderFilter, DateRangeFilter, Divider } from '@/components';
import { useFilterStore, useFilterActions } from '@/store';
import { countFilters } from '@/utils';

type NumberRange = [number, number];
type DateRange = [string, string];

const isNumberRange = (v: unknown): v is NumberRange =>
  Array.isArray(v) && v.length === 2 && typeof v[0] === 'number' && typeof v[1] === 'number';

const isDateISO = (s: unknown): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const isDateRange = (v: unknown): v is DateRange =>
  Array.isArray(v) && v.length === 2 && isDateISO(v[0]) && isDateISO(v[1]);

const isPartialDateRange = (v: unknown): v is DateRange =>
  Array.isArray(v) && v.length === 2 && v.every((item) => typeof item === 'string');

const getSelectedCheckboxOptions = (
  map: Record<string, string[] | NumberRange | DateRange | undefined>,
  key: string,
): string[] => {
  const v = map[key];
  return Array.isArray(v) && !isNumberRange(v) && !isDateRange(v) ? v : [];
};

interface FilterProps {
  filterData?: FilterSet | null;
  mobile?: boolean;
}

export function Filter({ filterData, mobile }: FilterProps) {
  const { activeFilters } = useFilterStore();
  const { toggleFilterOption, clearFilter, clearFilters, setSliderValue, setDateRangeValue } = useFilterActions();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (filterData?.length) {
      const initial = Object.fromEntries(filterData.map((section, i) => [section.title, i === 0]));
      setOpenSections(initial);
    }
  }, [filterData]);

  const toggleSection = (title: string, open: boolean) => {
    setOpenSections((prev) => ({ ...prev, [title]: open }));
  };

  const totalFilters = countFilters(filterData, activeFilters);

  return (
    <aside className="content-start space-y-5">
      {!mobile && (
        <>
          <FilterHeader totalFilters={totalFilters} clearFilters={clearFilters} />
          <Divider className="mx-[3px] w-[calc(100%-6px)]" />
        </>
      )}
      {filterData &&
        filterData.map((category) => {
          const { title, options, type, min, max, step, tooltip } = category;
          const selectedOptions = getSelectedCheckboxOptions(activeFilters, title);
          const selectedCount =
            type === 'slider' || type === 'date-range' ? (activeFilters[title] ? 1 : 0) : selectedOptions.length;
          const isOpen = !!openSections[title];

          return (
            <>
              <FilterSection
                key={title}
                title={title}
                selectedCount={selectedCount}
                isOpen={isOpen}
                onOpenChange={(open) => toggleSection(title, open)}
                tooltip={tooltip}
              >
                {type === 'slider' ? (
                  (() => {
                    const minV = min ?? 0;
                    const maxV = max ?? 100;
                    const stepV = step ?? 1;
                    const current = activeFilters[title] || [minV, maxV];
                    const defaultRange: NumberRange = isNumberRange(current) ? current : [minV, maxV];
                    return (
                      <SliderFilter
                        title={title}
                        min={minV}
                        max={maxV}
                        step={stepV}
                        value={defaultRange}
                        onValueCommit={(value) => {
                          const [start = minV, end = maxV] = value;
                          if (start != minV || end != maxV) {
                            const next: NumberRange = [start, end];
                            setSliderValue(title, next);
                          } else {
                            clearFilter(title);
                          }
                        }}
                      />
                    );
                  })()
                ) : type === 'date-range' ? (
                  (() => {
                    const current = activeFilters[title];
                    const value: DateRange = isPartialDateRange(current) ? current : ['', ''];
                    const [start, end] = value;

                    const setStartDate = (startDate: Date | undefined) => {
                      const endValue = end || '';

                      if (!startDate) {
                        if (!endValue) {
                          clearFilter(title);
                        } else {
                          setDateRangeValue(title, ['', endValue]);
                        }
                      } else {
                        const next = format(startDate, 'yyyy-MM-dd');
                        setDateRangeValue(title, [next, endValue]);
                      }
                    };

                    const setEndDate = (endDate: Date | undefined) => {
                      const startValue = start || '';
                      if (!endDate) {
                        if (!startValue) {
                          clearFilter(title);
                        } else {
                          setDateRangeValue(title, [startValue, '']);
                        }
                      } else {
                        const next = format(endDate, 'yyyy-MM-dd');
                        setDateRangeValue(title, [startValue, next]);
                      }
                    };

                    return (
                      <DateRangeFilter value={value} onStartDateChange={setStartDate} onEndDateChange={setEndDate} />
                    );
                  })()
                ) : (
                  <CheckboxFilter
                    title={title}
                    options={options || []}
                    selectedOptions={selectedOptions}
                    toggleFilterOption={toggleFilterOption}
                  />
                )}              
              </FilterSection>
              <Divider />
            </>
          );
        })}
    </aside>
  );
}
